// components/RichEditor.tsx
"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import HardBreak from "@tiptap/extension-hard-break";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { Extension } from "@tiptap/core";
import { useFormikContext, useField } from "formik"; // ✅ Formik wiring
import { mergeAttributes } from "@tiptap/core";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import styles from "./RichEditor.module.scss";

type Props = {
  initialJSON?: any;
  onChange?: (json: any, html: string) => void;
  maxChars?: number;
};
type TiptapRenderHTML = {
  HTMLAttributes: Record<string, any>;
};
/* ---------- helpers to build clean HTML blocks on paste ---------- */

function esc(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
const INTERNAL_DOMAIN = "dentopia.marketing";
// Helper function to determine if link should open in new tab
function getLinkAttributes(href: string | null): {
  target?: string;
  rel: string;
} {
  if (!href) return { rel: "noopener noreferrer" };

  const trimmed = href.trim();

  // Relative links are internal
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("?")
  ) {
    return { rel: "noopener noreferrer" };
  }

  // mailto / tel should NOT be target blank, and NOT nofollow
  if (/^(mailto:|tel:)/i.test(trimmed)) {
    return { rel: "noopener noreferrer" };
  }

  // Same-domain absolute URLs are internal
  try {
    const url = new URL(trimmed);
    if (
      url.hostname === INTERNAL_DOMAIN ||
      url.hostname === `www.${INTERNAL_DOMAIN}` ||
      url.hostname.endsWith(`.${INTERNAL_DOMAIN}`)
    ) {
      return { rel: "noopener noreferrer" };
    }
  } catch {
    // parsing failed => treat as external-safe
  }

  // External links
  return { target: "_blank", rel: "noopener noreferrer nofollow" };
}

function linkify(htmlText: string) {
  // URLs
  htmlText = htmlText.replace(/(https?:\/\/[^\s<]+)(?![^<]*>)/g, (m) => {
    const attrs = getLinkAttributes(m);
    const target = attrs.target ? ` target="${attrs.target}"` : "";
    return `<a href="${m}"${target} rel="${attrs.rel}">${m}</a>`;
  });
  // emails - always internal, no target
  htmlText = htmlText.replace(
    /\b([^\s<]+@[^\s<]+\.[^\s<]+)\b/g,
    (m) => `<a href="mailto:${m}" rel="noopener noreferrer">${m}</a>`
  );
  // phone-ish - always internal, no target
  htmlText = htmlText.replace(
    /\b(\+?[0-9][0-9\-()\s]{5,})\b/g,
    (m) => `<a href="tel:${m}" rel="noopener noreferrer">${m}</a>`
  );
  return htmlText;
}

// Plain text -> proper <p>, <ul>/<ol>, with first non-empty line bold
function plainTextToSimpleHTML(text: string): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: string[] = [];
  let i = 0;
  let firstBoldDone = false;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    if (!line) {
      i++;
      continue;
    }

    // horizontal rule lines like --- or ———
    if (/^(-{3,}|—{3,})$/.test(line)) {
      blocks.push("<hr>");
      i++;
      continue;
    }

    // bullet list block
    if (/^[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        const item = lines[i].trim().replace(/^[-*•]\s+/, "");
        items.push(`<li>${linkify(esc(item))}</li>`);
        i++;
      }
      blocks.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    // ordered list block
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const item = lines[i].trim().replace(/^\d+\.\s+/, "");
        items.push(`<li>${linkify(esc(item))}</li>`);
        i++;
      }
      blocks.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    // normal paragraph; first one bold
    const linked = linkify(esc(raw.trim()));
    if (!firstBoldDone) {
      blocks.push(`<p><strong>${linked}</strong></p>`);
      firstBoldDone = true;
    } else {
      blocks.push(`<p>${linked}</p>`);
    }
    i++;
  }

  return blocks.join("").replace(/<p>\s*<\/p>/g, "");
}

// HTML -> strip unsafe stuff, then convert to text with line breaks,
// then reuse the plain-text -> blocks path (fixes "single <p><strong>…<br>…" from Docs)
function sanitizePastedHTML(raw: string): string {
  if (!raw) return "";
  const wrap = document.createElement("div");
  wrap.innerHTML = raw;

  // remove dangerous/irrelevant nodes
  wrap
    .querySelectorAll(
      "script,style,meta,link,svg,object,embed,iframe,form,button"
    )
    .forEach((el) => el.remove());

  // normalize <div> to paragraph-ish for text extraction
  wrap.querySelectorAll("div").forEach((div) => {
    const p = document.createElement("p");
    p.innerHTML = div.innerHTML;
    div.replaceWith(p);
  });

  // turn <br> into newline characters for correct line splitting
  wrap.querySelectorAll("br").forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // add blank lines around paragraphs so we get paragraph breaks
  wrap.querySelectorAll("p").forEach((p) => {
    p.prepend(document.createTextNode("\n"));
    p.append(document.createTextNode("\n"));
  });

  // final plain text
  const text = (wrap.textContent || "").replace(/\u00A0/g, " ").trim();

  // rebuild into clean blocks (p/ul/ol/li) and relink urls/emails/phones
  return plainTextToSimpleHTML(text);
}

/* ------------------------------ component ------------------------------ */

export default function RichEditor({
  initialJSON,
  onChange,
  maxChars = 0,
}: Props) {
  // ✅ Assume fixed field names used in your form
  const NAME_JSON = "content_json";
  const NAME_HTML = "content_html";

  // ✅ Formik context + field (for errors/touched)
  const { setFieldValue, setFieldTouched, values } = useFormikContext<any>();
  const [, meta] = useField(NAME_JSON);

  // ✅ Use Formik value if present; fallback to prop; else empty paragraph
  const initialDoc = values?.[NAME_JSON] ??
    initialJSON ?? { type: "doc", content: [{ type: "paragraph" }] };

  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);

  // MUI Dialog state
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlValue, setUrlValue] = useState("");

  const handleLinkAction = () => {
    if (!editorRef.current) return;
    const previous = editorRef.current.getAttributes("link").href as
      | string
      | undefined;
    setUrlValue(previous ?? "");
    setUrlDialogOpen(true);
  };

  const handleUrlSubmit = () => {
    if (!editorRef.current) return;
    const raw = urlValue.trim();
    if (raw) {
      // Check if it's a relative path first
      const isRelativePath = /^[/#?]/.test(raw);
      const hasProtocol = /^(https?:|mailto:|tel:)/i.test(raw);
      const href = isRelativePath || hasProtocol ? raw : `https://${raw}`;
      editorRef.current
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href })
        .run();
    }
    setUrlDialogOpen(false);
    setUrlValue("");
  };

  const handleUrlCancel = () => {
    setUrlDialogOpen(false);
    setUrlValue("");
  };

  // Custom Link extension with dynamic attributes
  // Custom Link extension with dynamic attributes

  const BASE_URL = "https://www.dentopia.marketing";
  const INTERNAL_HOST = new URL(BASE_URL).hostname; // www.nuyu-dental.co.uk
  const INTERNAL_ROOT = INTERNAL_HOST.replace(/^www\./, ""); // nuyu-dental.co.uk

  function isInternalHref(href: string) {
    const t = href.trim();

    // relative is internal
    if (t.startsWith("/") || t.startsWith("#") || t.startsWith("?"))
      return true;

    // mailto/tel are not "external web"
    if (/^(mailto:|tel:)/i.test(t)) return true;

    try {
      const url = new URL(t);
      const h = url.hostname.replace(/^www\./, "");
      return h === INTERNAL_ROOT || h.endsWith(`.${INTERNAL_ROOT}`);
    } catch {
      return false;
    }
  }

  function getLinkAttributes(href: string | null): {
    target?: string;
    rel: string;
  } {
    if (!href) return { rel: "noopener noreferrer" };

    const trimmed = href.trim();

    // internal / mailto / tel
    if (isInternalHref(trimmed) || /^(mailto:|tel:)/i.test(trimmed)) {
      return { rel: "noopener noreferrer" };
    }

    // external
    return { target: "_blank", rel: "noopener noreferrer nofollow" };
  }

  const CustomLink = Link.extend({
    addOptions() {
      return {
        ...Link.options,
        HTMLAttributes: {
          class: "tiptap-link",
        },
      };
    },

    renderHTML({ HTMLAttributes }: TiptapRenderHTML) {
      const href = HTMLAttributes.href as string | undefined;
      const { target, rel } = getLinkAttributes(href ?? null);

      return [
        "a",
        {
          ...HTMLAttributes,
          rel,
          ...(target ? { target } : {}),
        },
        0,
      ];
    },
  });

  const Shortcuts = Extension.create({
    name: "shortcuts",
    addKeyboardShortcuts() {
      return {
        "Mod-b": () => this.editor.chain().focus().toggleBold().run(),
        "Mod-i": () => this.editor.chain().focus().toggleItalic().run(),
        "Mod-u": () => this.editor.chain().focus().toggleUnderline().run(),
        "Mod-e": () => this.editor.chain().focus().toggleCode().run(),
        "Mod-k": () => {
          // Use the same logic as handleLinkAction but directly here
          const previous = this.editor.getAttributes("link").href as
            | string
            | undefined;
          setUrlValue(previous ?? "");
          setUrlDialogOpen(true);
          return true;
        },
        "Mod-Alt-2": () =>
          this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
        "Mod-Alt-3": () =>
          this.editor.chain().focus().toggleHeading({ level: 3 }).run(),
        "Mod-Alt-4": () =>
          this.editor.chain().focus().toggleHeading({ level: 4 }).run(),
        "Mod-Alt-5": () =>
          this.editor.chain().focus().toggleHeading({ level: 5 }).run(),
        "Mod-Alt-6": () =>
          this.editor.chain().focus().toggleHeading({ level: 6 }).run(),
        "Shift-Enter": () => this.editor.chain().focus().setHardBreak().run(),
      } as const;
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Heading.configure({ levels: [2, 3, 4, 5, 6] }),
      Underline,
      CustomLink.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        protocols: ["http", "https", "mailto", "tel"],
      }), // align only paragraphs
      TextAlign.configure({ types: ["paragraph"] }),
      Placeholder.configure({
        placeholder:
          "Write your post. Use headings (H2-H6) to structure your content.",
      }),
      CharacterCount.configure({ limit: maxChars || null }),
      HardBreak,
      HorizontalRule,
      Shortcuts,
    ],
    editorProps: {
      attributes: { class: styles.editorSurface },
      // ⭐️ Build proper blocks on paste (Docs/Word/Plain)
      handlePaste: (_view: any, event: ClipboardEvent) => {
        const html = event.clipboardData?.getData("text/html") ?? "";
        const text = event.clipboardData?.getData("text/plain") ?? "";

        if (html && html.trim()) {
          const clean = sanitizePastedHTML(html);
          if (clean) {
            event.preventDefault();
            editorRef.current?.commands.insertContent(clean);
            return true;
          }
          return false;
        }

        if (text && text.trim()) {
          const converted = plainTextToSimpleHTML(text);
          event.preventDefault();
          editorRef.current?.commands.insertContent(converted);
          return true;
        }

        return false;
      },
    },
    content: initialDoc,
    onUpdate({ editor }) {
      const json = editor.getJSON();
      const html = editor.getHTML();
      // ✅ keep your original callback
      onChange?.(json, html);
      // ✅ sync to Formik
      setFieldValue(NAME_JSON, json);
      setFieldValue(NAME_HTML, html ?? "");
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // mark field touched on blur so Yup error shows
  const handleBlur = useCallback(() => {
    setFieldTouched(NAME_JSON, true, true);
  }, [setFieldTouched]);

  // Force toolbar to re-render when selection or transactions change
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const rerender = () => forceUpdate((x) => x + 1);
    editor.on("selectionUpdate", rerender);
    editor.on("transaction", rerender);
    return () => {
      editor.off("selectionUpdate", rerender);
      editor.off("transaction", rerender);
    };
  }, [editor]);

  if (!editor) return null;

  const Btn = ({
    active,
    onClick,
    label,
    title,
    disabled,
  }: {
    active?: boolean;
    onClick: () => void;
    label: React.ReactNode;
    title?: string;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      className={styles.btn}
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      aria-pressed={active ? "true" : "false"}
      data-active={active ? "true" : "false"}
    >
      {label}
    </button>
  );

  const showError = meta.touched && !!meta.error;

  return (
    <div className={styles.wrapper}>
      <div className={styles.editorBox}>
        <div className={styles.toolbar}>
          <Btn
            title="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            label={<b>B</b>}
          />
          <Btn
            title="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            label={<i>I</i>}
          />
          <Btn
            title="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            label={<u>U</u>}
          />

          <span className={styles.divider} />

          <Btn
            title="Heading 2"
            active={editor.isActive("heading", { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            label="H2"
          />
          <Btn
            title="Heading 3"
            active={editor.isActive("heading", { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            label="H3"
          />
          <Btn
            title="Heading 4"
            active={editor.isActive("heading", { level: 4 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 4 }).run()
            }
            label="H4"
          />
          <Btn
            title="Heading 5"
            active={editor.isActive("heading", { level: 5 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 5 }).run()
            }
            label="H5"
          />
          <Btn
            title="Heading 6"
            active={editor.isActive("heading", { level: 6 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 6 }).run()
            }
            label="H6"
          />

          <span className={styles.divider} />

          <Btn
            title="Bullet List"
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            label="• List"
          />
          <Btn
            title="Ordered List"
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            label="1. List"
          />
          <Btn
            title="Blockquote"
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            label="❝"
          />
          <Btn
            title="Code Block"
            active={editor.isActive("codeBlock")}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            label={"</>"}
          />

          <span className={styles.divider} />

          <Btn
            title="Align Left"
            active={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            label="⟸"
          />
          <Btn
            title="Align Center"
            active={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            label="≡"
          />
          <Btn
            title="Align Right"
            active={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            label="⟹"
          />

          <span className={styles.divider} />

          <Btn title="Add or Edit Link" onClick={handleLinkAction} label="🔗" />
          <Btn
            title="Remove Link"
            onClick={() =>
              editor.chain().focus().extendMarkRange("link").unsetLink().run()
            }
            label="Unlink"
          />

          <span className={styles.divider} />

          <Btn
            title="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            label="↶"
          />
          <Btn
            title="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            label="↷"
          />
        </div>

        {/* Wrap to capture blur for touched state */}
        <div className={styles.editorScroll} onBlur={handleBlur}>
          <EditorContent editor={editor} />
        </div>
      </div>
      <div className={styles.counter}>
        {(() => {
          const text = editor.getText();
          const count = text.trim() ? text.trim().split(/\s+/).length : 0;
          return `${count} words`;
        })()}
        {maxChars
          ? ` • ${editor.storage.characterCount.characters()}/${maxChars} characters`
          : ""}
      </div>

      {/* ✅ Visible validation error from Yup (content_json) */}
      {showError ? (
        <div className={styles.error}>{meta.error as unknown as string}</div>
      ) : null}

      {/* MUI Dialog for URL input */}
      <Dialog
        open={urlDialogOpen}
        onClose={handleUrlCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleUrlSubmit();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                handleUrlCancel();
              }
            }}
            inputProps={{ spellCheck: "false", autoCapitalize: "off" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUrlCancel}>Cancel</Button>
          <Button onClick={handleUrlSubmit} variant="contained">
            Add Link
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
